import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  useBusinessCategories,
  useBusinessProducts,
} from "../../../hooks/useBusinessCatalog";

const ProductRuleCheckbox = ({ checked, label, onToggle, variant }) => {
  const isChecked = Boolean(checked);

  const handleToggle = () => {
    if (!onToggle) {
      return;
    }
    onToggle();
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    handleToggle();
  };

  const baseColorClasses =
    variant === "exclude"
      ? "border-red-500/50 data-[checked=true]:bg-red-500 data-[checked=true]:border-red-500"
      : "border-blue-500/50 data-[checked=true]:bg-blue-500 data-[checked=true]:border-blue-500";

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isChecked}
      aria-label={label}
      tabIndex={0}
      data-checked={isChecked}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border bg-gray-950 transition-colors focus:ring-2 focus:ring-blue-500 focus:outline-none ${baseColorClasses}`}
    >
      {isChecked ? <Check className="h-3.5 w-3.5 text-white" /> : null}
    </button>
  );
};

const collectCategoryIds = (categories) => {
  const ids = new Set();
  const stack = Array.isArray(categories) ? [...categories] : [];

  while (stack.length > 0) {
    const node = stack.pop();
    if (!node || typeof node !== "object") {
      continue;
    }

    const id = Number(node.id);
    if (Number.isFinite(id)) {
      ids.add(id);
    }

    const subcategories = Array.isArray(node.subcategories)
      ? node.subcategories
      : Array.isArray(node.children)
        ? node.children
        : [];

    if (subcategories.length > 0) {
      stack.push(...subcategories);
    }
  }

  return ids;
};

const normalizeRulesProductsValue = (value) => {
  if (!Array.isArray(value)) {
    return new Map();
  }

  const map = new Map();

  value.forEach((item) => {
    if (typeof item === "number") {
      map.set(item, true);
      return;
    }

    if (!item || typeof item !== "object") {
      return;
    }

    const id = Number(item.id);
    if (!Number.isFinite(id)) {
      return;
    }

    const includeFromItem =
      typeof item.include === "boolean"
        ? item.include
        : typeof item?.pivot?.include === "boolean"
          ? item.pivot.include
          : true;

    map.set(id, includeFromItem);
  });

  return map;
};

const buildRulesProductsPayload = (selectionMap) => {
  const entries = Array.from(selectionMap.entries());

  if (entries.length === 0) {
    return null;
  }

  return entries.map(([id, include]) => ({
    id,
    include,
    is_condition: true,
  }));
};

const PromotionRulesProducts = ({
  selectedBusinessIds,
  businesses,
  value,
  onChange,
}) => {
  const eligibleBusinesses = useMemo(() => {
    const ids = new Set(
      Array.isArray(selectedBusinessIds)
        ? selectedBusinessIds.map((id) => Number(id))
        : [],
    );

    const list = Array.isArray(businesses) ? businesses : [];

    return list
      .filter((business) => ids.has(Number(business?.id)))
      .map((business) => ({
        id: Number(business.id),
        name: String(business.name || `Business ${business.id}`),
      }))
      .filter((business) => Number.isFinite(business.id));
  }, [businesses, selectedBusinessIds]);

  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (eligibleBusinesses.length === 0) {
      setSelectedBusinessId("");
      return;
    }

    if (
      eligibleBusinesses.some(
        (b) => String(b.id) === String(selectedBusinessId),
      )
    ) {
      return;
    }

    setSelectedBusinessId(String(eligibleBusinesses[0].id));
  }, [eligibleBusinesses, selectedBusinessId]);

  const { data: categoriesData, isLoading: isLoadingCategories } =
    useBusinessCategories(selectedBusinessId, {
      enabled: Boolean(selectedBusinessId),
    });

  const allowedCategoryIds = useMemo(
    () => collectCategoryIds(categoriesData),
    [categoriesData],
  );

  // Load products only after categories have been loaded for the selected business.
  const shouldLoadProducts =
    Boolean(selectedBusinessId) && !isLoadingCategories;

  const { data: productsData, isLoading: isLoadingProducts } =
    useBusinessProducts(selectedBusinessId, {
      enabled: shouldLoadProducts,
    });

  const products = useMemo(() => {
    const list = Array.isArray(productsData) ? productsData : [];
    const query = search.trim().toLowerCase();

    const normalized = list
      .map((product) => {
        const id = Number(product?.id);
        if (!Number.isFinite(id)) {
          return null;
        }

        const categoryIdRaw =
          product?.category_id ??
          product?.categoryId ??
          product?.category?.id ??
          product?.category?.category_id;
        const categoryId = Number(categoryIdRaw);

        return {
          id,
          name: String(product?.name || `Product ${id}`),
          enabled: product?.enabled !== false,
          categoryId: Number.isFinite(categoryId) ? categoryId : null,
        };
      })
      .filter(Boolean)
      .filter((product) => product.enabled);

    const filteredByCategories =
      allowedCategoryIds.size > 0
        ? normalized.filter((product) => {
            // If we don't have a category id on the product, keep it (can't reliably filter).
            if (!Number.isFinite(product.categoryId)) {
              return true;
            }
            return allowedCategoryIds.has(product.categoryId);
          })
        : normalized;

    if (!query) {
      return filteredByCategories;
    }

    return filteredByCategories.filter((product) =>
      product.name.toLowerCase().includes(query),
    );
  }, [productsData, search, allowedCategoryIds]);

  const selectionMap = useMemo(
    () => normalizeRulesProductsValue(value),
    [value],
  );

  const setProductSelection = (productId, nextInclude) => {
    const nextMap = new Map(selectionMap);

    if (nextInclude === null) {
      nextMap.delete(productId);
    } else {
      nextMap.set(productId, nextInclude);
    }

    const nextPayload = buildRulesProductsPayload(nextMap);
    if (onChange) {
      onChange(nextPayload);
    }
  };

  if (!Array.isArray(selectedBusinessIds) || selectedBusinessIds.length === 0) {
    return (
      <div className="text-sm text-gray-400">
        Select at least one store in the promotion to configure product rules.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-400">
            Business
          </label>
          <div className="relative">
            <select
              value={selectedBusinessId}
              onChange={(event) => {
                setSelectedBusinessId(event.target.value);
              }}
              className="w-full cursor-pointer appearance-none rounded-lg border border-gray-800 bg-gray-950 py-2.5 pr-10 pl-4 text-white transition-all outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500"
              aria-label="Select business for products"
            >
              {eligibleBusinesses.map((business) => (
                <option key={business.id} value={business.id}>
                  {business.name}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-gray-500"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-gray-400">
            Search products
          </label>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Type to filter…"
            className="w-full rounded-lg border border-gray-800 bg-gray-950 px-4 py-2.5 text-white placeholder-gray-600 transition-all outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500"
            aria-label="Search products"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-200">
            Choose product to include
          </h4>
          <div className="max-h-[46vh] overflow-x-auto overflow-y-auto rounded-lg border border-gray-800 bg-gray-950/60 p-2">
            {isLoadingCategories && (
              <p className="p-2 text-xs text-gray-500">Loading categories...</p>
            )}
            {isLoadingProducts && (
              <p className="p-2 text-xs text-gray-500">Loading products...</p>
            )}
            {!isLoadingCategories &&
              !isLoadingProducts &&
              products.length === 0 && (
                <p className="p-2 text-xs text-gray-500">No products found.</p>
              )}
            {!isLoadingCategories &&
              !isLoadingProducts &&
              products.map((product) => {
                const isSelected = selectionMap.get(product.id) === true;

                return (
                  <div
                    key={`include-${product.id}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Toggle include ${product.name}`}
                    onClick={(event) => {
                      if (event.target.closest("button")) {
                        return;
                      }
                      setProductSelection(product.id, isSelected ? null : true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") {
                        return;
                      }
                      event.preventDefault();
                      setProductSelection(product.id, isSelected ? null : true);
                    }}
                    className="flex min-w-max cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <ProductRuleCheckbox
                      checked={isSelected}
                      variant="include"
                      label={`Include ${product.name}`}
                      onToggle={() =>
                        setProductSelection(
                          product.id,
                          isSelected ? null : true,
                        )
                      }
                    />
                    <span className="text-sm leading-5 whitespace-nowrap text-gray-200">
                      {product.name}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-200">
            Choose product to exclude
          </h4>
          <div className="max-h-[46vh] overflow-x-auto overflow-y-auto rounded-lg border border-gray-800 bg-gray-950/60 p-2">
            {isLoadingCategories && (
              <p className="p-2 text-xs text-gray-500">Loading categories...</p>
            )}
            {isLoadingProducts && (
              <p className="p-2 text-xs text-gray-500">Loading products...</p>
            )}
            {!isLoadingCategories &&
              !isLoadingProducts &&
              products.length === 0 && (
                <p className="p-2 text-xs text-gray-500">No products found.</p>
              )}
            {!isLoadingCategories &&
              !isLoadingProducts &&
              products.map((product) => {
                const isSelected = selectionMap.get(product.id) === false;

                return (
                  <div
                    key={`exclude-${product.id}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Toggle exclude ${product.name}`}
                    onClick={(event) => {
                      if (event.target.closest("button")) {
                        return;
                      }
                      setProductSelection(
                        product.id,
                        isSelected ? null : false,
                      );
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") {
                        return;
                      }
                      event.preventDefault();
                      setProductSelection(
                        product.id,
                        isSelected ? null : false,
                      );
                    }}
                    className="flex min-w-max cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <ProductRuleCheckbox
                      checked={isSelected}
                      variant="exclude"
                      label={`Exclude ${product.name}`}
                      onToggle={() =>
                        setProductSelection(
                          product.id,
                          isSelected ? null : false,
                        )
                      }
                    />
                    <span className="text-sm leading-5 whitespace-nowrap text-gray-200">
                      {product.name}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Selecting a product in “Include” sets{" "}
        <span className="text-gray-300">include=true</span>. Selecting in
        “Exclude” sets <span className="text-gray-300">include=false</span>.
        Unchecking removes it from the rule.
      </p>
    </div>
  );
};

export default PromotionRulesProducts;
